import { useState } from "react";
import { Minus, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { api, errMsg, money } from "../../lib/api";
import { MerchImage } from "./MerchImage";
import { useCart } from "../../lib/cart";
import { useAuth } from "../../lib/auth";

export const CartDrawer = () => {
  const { items, setQty, total, open, setOpen } = useCart();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const mail = email || (user && user.email) || "";

  const pay = async () => {
    if (!/^\S+@\S+\.\S+$/.test(mail)) return toast.error("Enter your email for the receipt");
    setBusy(true);
    try {
      const { checkout_url } = await api.checkout(
        items.map(({ ref_id, quantity, size }) => ({ type: "merch", ref_id, quantity, size })),
        mail,
      );
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-[#0d0f14] border-l border-[#222634] text-slate-100 w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-cond uppercase tracking-widest text-2xl text-white">Your cart</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto mt-6 space-y-4">
          {items.length === 0 && <p data-testid="cart-empty" className="text-slate-400 text-sm">Your cart is empty.</p>}
          {items.map((it) => (
            <div key={`${it.ref_id}-${it.size}`} data-testid={`cart-item-${it.ref_id}-${it.size || "one"}`} className="flex gap-4 card-surface rounded-xl p-3">
              <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0"><MerchImage m={it} small /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{it.name}</p>
                {it.size && <p className="text-xs text-slate-400">Size {it.size}</p>}
                <div className="flex items-center gap-2 mt-2">
                  <button className="btn-line rounded-md p-1" onClick={() => setQty(it, it.quantity - 1)} data-testid="cart-qty-minus"><Minus className="w-3 h-3" /></button>
                  <span className="font-mono-ui text-sm w-6 text-center">{it.quantity}</span>
                  <button className="btn-line rounded-md p-1" onClick={() => setQty(it, it.quantity + 1)} data-testid="cart-qty-plus"><Plus className="w-3 h-3" /></button>
                  <button className="ml-auto text-slate-500 hover:text-red-400" onClick={() => setQty(it, 0)} data-testid="cart-remove"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <span className="font-mono-ui text-sm">{money(it.price * it.quantity)}</span>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div className="border-t border-[#222634] pt-5 space-y-4">
            <div className="flex justify-between font-cond uppercase tracking-widest text-lg">
              <span>Subtotal</span><span data-testid="cart-total" className="text-lime">{money(total)}</span>
            </div>
            <input className="field" type="email" placeholder="Email for receipt" value={mail} onChange={(e) => setEmail(e.target.value)} data-testid="cart-email-input" />
            <button disabled={busy} onClick={pay} data-testid="cart-checkout-button" className="btn-lime w-full rounded-full py-3.5 font-cond uppercase tracking-widest font-bold text-lg flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Checkout
            </button>
            <p className="text-[11px] text-slate-500 text-center">Shipping address is collected on the secure Stripe page.</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
